'use client';

import { useState } from 'react';
import { Download, Eye, MoreVertical, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatFileSize } from '@kit/shared/lib/file-utilities';
import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { useConfirmDialog } from '@kit/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { Trans } from '@kit/ui/trans';

import { useDocumentDownload } from '../hooks/use-document-download';
import type { Document, DocumentDisplayProps } from '../types';
import { formatDocumentDate } from '../utils/date-utils';
import { DocumentThumbnail } from './document-thumbnail';

interface DocumentCardProps<
  T extends Document = Document,
> extends DocumentDisplayProps<T> {
  renderBadge?: (document: T) => React.ReactNode;
  renderMeta?: (document: T) => React.ReactNode;
  /** Show download button inline at the bottom instead of in dropdown. Defaults to false. */
  inlineDownload?: boolean;
}

export function DocumentCard<T extends Document = Document>({
  document,
  onView,
  onDelete,
  showActions = true,
  className = '',
  renderBadge,
  renderMeta,
  inlineDownload = false,
}: DocumentCardProps<T>) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { download, isDownloading } = useDocumentDownload();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { t } = useTranslation();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete) return;

    const confirmed = await confirm({
      title: t('documents:confirmDeleteTitle'),
      description: t('documents:confirmDelete', {
        fileName: document.fileName,
      }),
      confirmText: t('common:delete'),
      variant: 'destructive',
    });

    if (confirmed) {
      setIsDeleting(true);
      try {
        await onDelete();
      } catch (error) {
        console.error('Failed to delete document:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await download(document);
  };

  return (
    <>
      <Card
        className={`overflow-hidden transition-shadow hover:shadow-md ${className}`}
      >
        <div className="relative">
          <DocumentThumbnail document={document} onClick={onView} />
          {renderBadge && (
            <div className="absolute top-2 left-2">{renderBadge(document)}</div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h4
                className="flex-1 truncate text-sm font-medium"
                title={document.fileName}
              >
                {document.fileName}
              </h4>

              {showActions && !inlineDownload && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">
                        <Trans i18nKey="documents:card.openMenu" />
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onView}>
                      <Eye className="mr-2 h-4 w-4" />
                      <Trans i18nKey="documents:card.view" />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {isDownloading ? (
                        <Trans i18nKey="documents:card.downloading" />
                      ) : (
                        <Trans i18nKey="documents:card.download" />
                      )}
                    </DropdownMenuItem>
                    {onDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {isDeleting ? (
                            <Trans i18nKey="documents:card.deleting" />
                          ) : (
                            <Trans i18nKey="documents:card.delete" />
                          )}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {showActions && inlineDownload && onDelete && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">
                        <Trans i18nKey="documents:card.openMenu" />
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {isDeleting ? (
                        <Trans i18nKey="documents:card.deleting" />
                      ) : (
                        <Trans i18nKey="documents:card.delete" />
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <span>{formatFileSize(document.fileSize)}</span>
              {document.uploadedAt && (
                <>
                  <span>•</span>
                  <span>{formatDocumentDate(document.uploadedAt)}</span>
                </>
              )}
            </div>

            {renderMeta && renderMeta(document)}

            {document.description && (
              <p className="text-muted-foreground line-clamp-2 text-xs">
                {document.description}
              </p>
            )}

            {inlineDownload && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2 w-full"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                <Download className="mr-2 h-4 w-4" />
                {isDownloading ? (
                  <Trans i18nKey="documents:card.downloading" />
                ) : (
                  <Trans i18nKey="documents:card.download" />
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <ConfirmDialogComponent />
    </>
  );
}
